/** Epoch ms of a message `created` — router "YYYY-MM-DD HH:MM:SS" (UTC) or ISO. */
export const parseMessageTime = (created: string) =>
  Date.parse(created.substr(0, 10) + 'T' + created.substr(11, 8) + 'Z');

export function getMessageTime(created: string) {
  const datetime = new Date(parseMessageTime(created));
  const { date, time } = dateTime2ReadableString(datetime);

  return { date, time };
}

export const isSameDay = (d1: Date, d2: Date) => {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

function GetLocaleTimeString(datetime: Date) {
  let result = datetime.toLocaleTimeString();

  result = result.replace(/(.*)\D\d+/, '$1');

  return result;
}

export function dateTime2ReadableString(datetime: Date) {
  const result = {
    date: '',
    time: '',
  };

  if (datetime) {
    const today = new Date();

    const today_ms = today.getTime();
    const datetime_ms = datetime.getTime();
    if (datetime_ms > today_ms) {
      // This time in the future. This is impossible case. Consider it 'today'
      result.date = 'Today';
      result.time = GetLocaleTimeString(datetime);
    } else {
      const today_start = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        0,
        0,
        0
      );
      const today_start_ms = today_start.getTime();

      if (today_start_ms < datetime_ms) {
        // It's today
        result.date = 'Today';
        result.time = GetLocaleTimeString(datetime);
      } else if (today_start_ms - datetime_ms < 24 * 60 * 60 * 1000) {
        // It's yesterday
        result.date = 'Yesterday';
        result.time = GetLocaleTimeString(datetime);
      } else if (today_start_ms - datetime_ms < 7 * 24 * 60 * 60 * 1000) {
        // It's in a week
        const weekdays = [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ];
        result.date =
          weekdays[datetime.getDay()] +
          ' (' +
          datetime.toLocaleDateString() +
          ')';
        result.time = GetLocaleTimeString(datetime);
      } else {
        result.date = datetime.toLocaleDateString();
        result.time = GetLocaleTimeString(datetime);
      }
    }
  }

  return result;
}

export function elapsedTime(dateString: string): string {
  const inputDate = new Date(dateString + "Z");  // interpret input date string as UTC
  const currentDate = new Date();
  const diff = currentDate.getTime() - inputDate.getTime();

  // Constants for various time durations in milliseconds
  const SECOND = 1000;
  const MINUTE = 60 * SECOND;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;
  const APPROX_MONTH = 30 * DAY; // Approximation, as months can vary between 28-31 days
  const YEAR = 365 * DAY; // Not accounting for leap years

  // Calculating elapsed time in different units
  if (diff < HOUR) {
    const minutes = Math.floor(diff / MINUTE);
    return `${minutes}m`;
  } else if (diff < DAY) {
    const hours = Math.floor(diff / HOUR);
    return `${hours}h`;
  } else if (diff < APPROX_MONTH) {
    const days = Math.floor(diff / DAY);
    return `${days}d`;
  } else if (diff < YEAR) {
    const months = Math.floor(diff / APPROX_MONTH);
    return `${months}mo`;
  } else {
    const years = Math.floor(diff / YEAR);
    return `${years}yr`;
  }
}

const formatDate = (date: Date): string => {
  // Formatting to YYYY-MM-DD HH:MM:SS
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// Formats current date in UTC without the T + .sssZ from ISO standard
export const getCurrentFormattedDate = (): string => {
  return new Date().toISOString().split(".")[0].replace("T", " ");
};

export function timestampToFormattedDate(timestamp: string): string {
  // Convert timestamp to Date
  const date = new Date(parseInt(timestamp, 10)); 
  return formatDate(date);
}

export const isWithinFiveMinutes = (dateString: string): boolean => {
  const inputDate = new Date(dateString.replace(' ', 'T'));
  const currentDate = new Date();

  // Note: both these are in milliseconds
  const diff = Math.abs(currentDate.getTime() - inputDate.getTime());
  const fiveMin = 5 * 60 * 1000;

  return diff <= fiveMin;
};

